import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { RecordData, Service } from 'webapp-types';
import { MetricValueType } from 'webapp/enums/enums';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectComponent from 'webapp/components/form/select';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectFormatComponent from 'webapp/components/form/select-format';
import FormTextareaComponent from 'webapp/components/form/textarea';
import MetricEditEnsureUniquenessComponent from 'webapp/components/metric/edit/ensure-uniqueness';
import type { EnsureUniquenessSettings } from 'webapp/components/metric/edit/ensure-uniqueness';
import MetricEditFormatComponent from 'webapp/components/metric/edit/format';
import MetricEditHistoricalDataPersistenceComponent from 'webapp/components/metric/edit/historical-data-persistence';
import MetricEditInvertComponent from 'webapp/components/metric/edit/invert';
import MetricEditMetricTypePropertiesComponent from 'webapp/components/metric/edit/metric-type-properties';
import type { MetricTypePropertiesType } from 'webapp/components/metric/edit/metric-type-properties';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';
import VerificationToggleMetricComponent from 'webapp/components/verification/toggle/metric';

import type { Metric } from 'webapp/models-custom';

import type { OnAfterMetricEditFn } from 'webapp/services/model-manager/metrics';

const Tabs = {
	GENERAL: 1,
	ADVANCED: 2,
	VISUAL: 3,
};

interface Args {
	model: Metric;
	isOpenedFromQueryOrScriptForm?: boolean;
	onContentChange?: () => void;
	onSave?: (metricData: RecordData.Metric.Serialized) => void;
	onClose?: () => void;
	afterSave?: OnAfterMetricEditFn;
	isPeriodDataTypeHidden?: boolean;
	isAllValuesDataTypeHidden?: boolean;
}

// const GranulationsAvailableSection = {
// 	ALL: 1,
// 	SELECTED: 2,
// };

export default class MetricEditComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;
	@service declare infolayer: Service.Infolayer;
	@service declare request: Service.Request;
	@service declare popup: Service.Popup;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked submitInProgress = false;
	@tracked activeTabId = Tabs.GENERAL;
	@tracked descriptionExpanded = false;
	@tracked description = '';
	@tracked showMetricIdExistsWarning = false;
	// @tracked selectedGranulationsAvailableSection = GranulationsAvailableSection.ALL;
	@tracked changeFormatOptions: OptionRaw<string>[] = [];
	@tracked selectedChangeFormat: string;

	tabs = [
		{ id: Tabs.GENERAL, label: 'General' },
		{ id: Tabs.ADVANCED, label: 'Data aggregation' },
		{ id: Tabs.VISUAL, label: 'Visual preferences' },
	];

	// granulationsAvailableSectionOptions = [
	// 	{ value: GranulationsAvailableSection.ALL, label: 'All (recommended)' },
	// 	{ value: GranulationsAvailableSection.SELECTED, label: 'Only selected' },
	// ];

	// granulationsAvailableOptions = DateRange.predefinedGranulations.filter((item) => item.unit !== TimeUnit.TOTAL).map((item) => {
	// 	return {
	// 		value: item.unit,
	// 		label: item.id
	// 	};
	// });

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.hasTabs && this.args.isOpenedFromQueryOrScriptForm) {
			this.activeTabId = Tabs.VISUAL;
		}

		// default name, metricId, isHistoricalDataPersistenceEnabled
		if (this.args.model.isNew) {
			if (!this.args.model.name) {
				this.args.model.name = 'My metric';
				this.generateUniqueMetricId(this.args.model.name);
			}
		}

		if (
			!this.isDescriptionEditable ||
			this.args.model.isTypeMetricCalculation ||
			this.args.model.description ||
			!this.showName
		) {
			this.descriptionExpanded = true;
		}

		this.description = this.args.model.description || '';

		// this.selectedGranulationsAvailableSection = GranulationsAvailableSection[this.isAllGranulationsAvailableSelected ? 'ALL': 'SELECTED'];

		this.selectedChangeFormat =
			this.args.model.changeFormat || this.constantsService.defaultChangeFormat;
		this.updateChangeFormatOptions();

		schedule('afterRender', this, this.contentChanged); // to reposition popup
	}

	updateChangeFormatOptions(): void {
		this.changeFormatOptions = this.mmMetricsService.getChangeFormatValuesForChangeFunction(
			this.args.model.changeFunction,
		);
		if (!this.changeFormatOptions.find((item) => item.value === this.selectedChangeFormat)) {
			this.changeFormatOptions.push({
				value: this.selectedChangeFormat,
				label: this.selectedChangeFormat,
			});
		}
	}

	get isGeneralTabActive(): boolean {
		return this.activeTabId === Tabs.GENERAL;
	}

	get isAdvancedTabActive(): boolean {
		return this.activeTabId === Tabs.ADVANCED;
	}

	get isVisualTabActive(): boolean {
		return this.activeTabId === Tabs.VISUAL;
	}

	get hasTabs(): boolean {
		return !this.args.model.isTypeBasic;
	}

	get showSourceInfo(): boolean {
		return this.args.model.isTypeCustomPush;
	}

	get showEditWarning(): boolean {
		return (
			(this.args.model.isTypeCustomQuery || this.args.model.isTypeMetricCalculation) &&
			!this.args.isOpenedFromQueryOrScriptForm
		);
	}

	get showName(): boolean {
		// don't show name for QB edits
		return !(this.args.isOpenedFromQueryOrScriptForm && this.args.model.isNew);
	}

	get showMetricId(): boolean {
		return this.args.model.isTypeCustomPush;
	}

	get isNameDisabled(): boolean {
		return this.args.model.isTypeBasic || this.submitInProgress;
	}

	get isDescriptionEditable(): boolean {
		return !this.args.model.isTypeBasic;
	}

	get isDescriptionDisabled(): boolean {
		return !this.isDescriptionEditable || this.submitInProgress;
	}

	get isMetricIdEditable(): boolean {
		return this.args.model.isNew;
	}

	get isMetricIdDisabled(): boolean {
		return !this.isMetricIdEditable || this.submitInProgress;
	}

	get isAttributedDisabled(): boolean {
		return !this.args.model.isNew || this.submitInProgress;
	}

	get showIsAttributed(): boolean {
		return this.args.model.isTypeCustomPush;
	}

	get showDimensionKey(): boolean {
		return (
			this.showIsAttributed &&
			this.args.model.allowDimensions &&
			!this.args.model.isNew &&
			!!this.dimensionKey
		);
	}

	get dimensionKey(): string {
		return (this.args.model.id || '').split('|').pop() || '';
	}

	get attributedTooltipText(): string {
		let text =
			'Dimensions allow you to break up a metric by a common property and store multiple dimensions (attributes) under a single, attributed metric.';
		if (this.showReadMore) {
			text +=
				' <a href="https://developers.databox.com/dimensions" target="_blank">Learn more</a>';
		}
		return text;
	}

	get showSaveControls(): boolean {
		return !this.args.model.isTypeBasic;
	}

	get showEnsureUnique(): boolean {
		return this.args.model.metricType === MetricValueType.EVENT;
	}

	get sourceType() {
		return this.args.model.sourceType;
	}

	get showHistoricalDataPersistence() {
		return this.args.model.sourceType?.historicalDataPersistenceSupported;
	}

	get historicalDataPersistence(): boolean {
		return !!this.args.model.isHistoricalDataPersistenceEnabled;
	}

	get showDataAggregationSettings(): boolean {
		return (
			this.args.model.isTypeCustomPush ||
			(this.args.model.isTypeCustomQuery && !!this.args.model.source?.isSqlishConnector)
		);
	}

	get showDataAggregationSettingsWithDatasetsLimitation(): boolean {
		return this.showDataAggregationSettings && !this.args.model.source?.isTypeOfDataset;
	}

	get dailyZero(): boolean {
		// inverse of noReset
		return !this.args.model.noReset;
	}

	set dailyZero(value: boolean) {
		this.args.model.noReset = !value;
	}

	// get isAllGranulationsAvailableSelected(): boolean {
	// 	return this.args.model.granulationsAvailable.length === 0
	// 		|| this.args.model.granulationsAvailable.length === this.granulationsAvailableOptions.length;
	// }

	// get showAvailableGranulationsSelector(): boolean {
	// 	return this.selectedGranulationsAvailableSection === GranulationsAvailableSection.SELECTED;
	// }

	get isDefaultChangeFormatSelected(): boolean {
		return this.selectedChangeFormat === this.constantsService.defaultChangeFormat;
	}

	get changeFunctionOptions() {
		return this.constantsService.mkChangeFns;
	}

	get showLinkToDevelopersWebsite(): boolean {
		return (
			this.args.model.isNew &&
			!this.args.isOpenedFromQueryOrScriptForm &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins
		);
	}

	get showReadMore(): boolean {
		return !this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
	}

	get alreadyTakenMetricIds(): string[] {
		return this.mmMetricsService.getAlreadyTakenMetricIds(this.args.model.spaceAccessId);
	}

	contentChanged(): void {
		if (this.args.onContentChange) {
			this.args.onContentChange();
		}
	}

	generateUniqueMetricId(name: string): void {
		// when duplicating metrickey, ensure same metricId
		if (this.args.model.cloneFrom) {
			return;
		}

		const usedMetricIds = this.alreadyTakenMetricIds;

		const uniqueMetricId = this.mmMetricsService.generateUniqueMetricId(name, usedMetricIds);

		this.args.model.metricId = uniqueMetricId;
		this.onMetricIdChange();
	}

	get isVerificationDisabled(): boolean {
		return !this.args.model.canEdit && !this.args.model.canEditDefinition;
	}

	@action
	doEditWarningAction(): void {
		void this.popup.close(); // closes immediately

		const model = this.args.model;
		next(() => {
			schedule('afterRender', () => {
				model.edit();
			});
		});
	}

	@action
	onNameChange(e: InputEvent): void {
		if (!this.isMetricIdEditable) {
			return;
		}
		const name = (e.target as HTMLInputElement).value;
		this.generateUniqueMetricId(name);
	}

	@action
	onMetricIdChange(): void {
		const usedMetricIds = this.alreadyTakenMetricIds;
		this.showMetricIdExistsWarning = usedMetricIds.includes(this.args.model.metricId || '');
	}

	@action
	validateMetricId(): void {
		const isValid = this.mmMetricsService.validateMetricId(this.args.model.metricId || '');
		if (!isValid) {
			this.generateUniqueMetricId(this.args.model.metricId || '');
			this.infolayer.ok('Autocorrected an invalid internal metric key!');
		}
	}

	@action
	changeIsAttributed(value: boolean): void {
		this.args.model.isStandalone = !value;
		this.args.model.dimensions = value ? [{ key: 'dimension', value: 'Dimension' }] : [];
		this.generateUniqueMetricId(this.args.model.name);
	}

	@action
	updateDescription(): void {
		this.args.model.description = this.description || '';
	}

	@action
	changeEnsureUniquenessSettings(ensureUniquenessSettings: EnsureUniquenessSettings): void {
		this.args.model.ensureUnique = ensureUniquenessSettings.ensureUnique;
		this.args.model.isAppend = ensureUniquenessSettings.isAppend;
	}

	@action
	changeHistoricalDataPersistence(value: boolean | null) {
		this.args.model.isHistoricalDataPersistenceEnabled = value;
	}

	@action
	changeDailyZero(value: boolean): void {
		this.dailyZero = value;
	}

	@action
	changeHasFutureData(value: boolean): void {
		this.args.model.hasFutureData = value;
	}

	@action
	changeIsHistoryChanging(value: boolean): void {
		this.args.model.isHistoryChanging = value;
	}

	// @action
	// changeGranulationsAvailableSection(value: number): void {
	// 	this.selectedGranulationsAvailableSection = value;
	// 	if (value === GranulationsAvailableSection.ALL) {
	// 		this.args.model.granulationsAvailable = [];
	// 	}
	// }

	// @action
	// changeGranulationsAvailable(value: number[]): void {
	// 	if (value.length === this.granulationsAvailableOptions.length) {
	// 		this.args.model.granulationsAvailable = [];
	// 	} else {
	// 		this.args.model.granulationsAvailable = value;
	// 	}
	// 	if (this.isAllGranulationsAvailableSelected) {
	// 		this.selectedGranulationsAvailableSection = GranulationsAvailableSection.ALL;
	// 	}
	// }

	@action
	updateMetricTypeProperties(metricTypeProperties: MetricTypePropertiesType): void {
		this.args.model.metricType = metricTypeProperties.metricType;
		this.args.model.granulationOrigin = metricTypeProperties.granulationOrigin;
		this.args.model.aggregationFunction = metricTypeProperties.aggregationFunction;

		if (this.args.model.metricType !== MetricValueType.EVENT) {
			this.args.model.ensureUnique = false; // reset to default FALSE value
			this.args.model.isAppend = false; // reset to default FALSE value
		}
	}

	@action
	changeChangeInvert(value: boolean): void {
		this.args.model.changeInvert = value;
	}

	@action
	changeFormat(value: string): void {
		this.args.model.format = value;
	}

	@action
	changeChangeFunction(value: number): void {
		this.args.model.changeFunction = value;

		// update format from change function
		const format = this.mmMetricsService.getChangeFormatFromChangeFunction(
			value,
			this.selectedChangeFormat,
		);
		this.changeChangeFormat(format);
		this.updateChangeFormatOptions();
	}

	@action
	changeChangeFormat(value: string | null): void {
		this.selectedChangeFormat = value || '';

		if (this.isDefaultChangeFormatSelected) {
			this.args.model.changeFormat = '';
		} else {
			this.args.model.changeFormat = value || '';
		}
	}

	@action
	addCustomChangeFormat(value: string): void {
		const newFormat = {
			value,
			label: value,
		};
		this.changeFormatOptions = [...this.changeFormatOptions, newFormat];
		this.changeChangeFormat(value);
	}

	@action
	expandDescription(): void {
		this.descriptionExpanded = true;
		schedule('afterRender', this, this.contentChanged);
	}

	@action
	selectTab(tabId: number): void {
		this.activeTabId = tabId;
		schedule('afterRender', this, this.contentChanged);
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		if (!this.args.model.validate()) {
			this.selectTab(Tabs.GENERAL);
		}

		const json = this.args.model.toSerializedJSON();

		if (this.args.onSave) {
			// don't save mk, just return the payload
			this.args.onSave(json);
			this.close();
		} else {
			this.submitInProgress = true;

			const isNew = this.args.model.isNew;

			const method = isNew ? 'POST' : 'PUT',
				url = '/metrics' + (isNew ? '' : `/${encodeURIComponent(this.args.model.key)}`);

			this.request
				.api<RecordData.Payload>(method, url, { metric: json })
				.then((reply) => {
					this.storeService.pushPayload(reply);
					this.infolayer.ok('Metric definition saved!');
					if (this.args.afterSave) {
						this.args.afterSave([this.args.model]);
					}
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.request.showAllValidationErrors.bind(this.request));
		}
	}

	@action
	close(): void {
		if (this.args.onClose) {
			this.args.onClose();
		}
	}
	<template>
		{{#if this.hasTabs}}
			<ul class="modalTabs modalTabs-2">
				{{#each this.tabs as |tab|}}
					<li
						class="navigation-button
							{{if (eq this.activeTabId tab.id) 'navigation-button--selected'}}"
					>
						<a role="button" {{on "click" (fn this.selectTab tab.id)}}>{{tab.label}}</a>
					</li>
				{{/each}}
			</ul>
		{{/if}}

		<div class="content">

			{{#if this.isGeneralTabActive}}
				<div class="tab-1">

					<VerificationToggleMetricComponent
						@metric={{@model}}
						@isDisabled={{this.isVerificationDisabled}}
					/>

					{{#if this.showEditWarning}}
						<div class="warning">
							<CommonUiIconComponent @name="AlertFill" @size="sm" />
							This metric is created with the
							{{if
								@model.isTypeMetricCalculation
								"Data calculations"
								"Metric builder"
							}}
							tool.
							<a {{on "click" this.doEditWarningAction}}>Edit metric setup</a>
						</div>
					{{/if}}

					{{#if this.showSourceInfo}}
						<div class="row parts-2">
							<div class="part">
								<FormInputComponent
									name="source_name"
									placeholder="Data source name"
									disabled={{true}}
									@value={{@model.source.title}}
								/>
							</div>
							<div class="part">
								<FormInputComponent
									name="source_token"
									placeholder="Push API token"
									disabled={{true}}
									@value={{@model.source.token}}
								/>
							</div>
						</div>
					{{/if}}

					{{#if this.showName}}
						<div class="row">
							<FormInputComponent
								name="name"
								placeholder="Display name"
								maxlength="50"
								disabled={{this.isNameDisabled}}
								@input={{this.onNameChange}}
								@value={{@model.name}}
							/>
							{{#unless this.descriptionExpanded}}
								<a class="expand-desc" {{on "click" this.expandDescription}}>
									+ Add description
								</a>
							{{/unless}}
							<RowErrorComponent @items={{@model.errors.name}} />
						</div>
					{{/if}}

					{{#if this.descriptionExpanded}}
						<div class="row">
							<FormTextareaComponent
								name="name"
								placeholder="Description"
								maxlength="250"
								disabled={{this.isDescriptionDisabled}}
								@value={{this.description}}
								@focusOut={{this.updateDescription}}
							/>
						</div>
					{{/if}}

					{{#if this.showIsAttributed}}
						<div class="row row-attributed parts-{{if this.showDimensionKey '2' '1'}}">
							<div class="part">
								<FormCheckboxComponent
									@name="attributed"
									@selected={{@model.allowDimensions}}
									@label="Dimensioned / attributed metric"
									@onChange={{this.changeIsAttributed}}
									@isDisabled={{this.isAttributedDisabled}}
									@item={{hash
										tooltip=this.attributedTooltipText
										tooltipMaxWidth=230
									}}
								/>
							</div>
							{{#if this.showDimensionKey}}
								<div class="part">
									<FormInputComponent
										name="dimension_key"
										placeholder="Dimension key"
										maxlength="100"
										disabled={{true}}
										@value={{this.dimensionKey}}
									/>
								</div>
							{{/if}}
						</div>
					{{/if}}

					{{#if this.showMetricId}}
						<div class="row">
							<FormInputComponent
								name="metric_id"
								placeholder="Internal metric key"
								maxlength="100"
								disabled={{this.isMetricIdDisabled}}
								@input={{this.onMetricIdChange}}
								@focusOut={{this.validateMetricId}}
								@value={{@model.metricId}}
							/>
							{{#if this.showMetricIdExistsWarning}}
								<p class="metric-id-warning">
									<CommonUiIconComponent @name="AlertFill" @size="sm" />
									This internal metric key identifier already exists in this data
									source. Existing data will be used for this metric.
								</p>
							{{/if}}
							<p>
								Internal metric key is the identification key used for sending data
								over the PushAPI and defines the warehouse storage bucket, unique
								within each data source. Once created it can't be edited. You can
								create multiple metrics on the same internal metric key.
								{{#if this.showReadMore}}
									<a
										href="https://developers.databox.com/create-metric"
										target="_blank"
									>
										Read more
									</a>
								{{/if}}
							</p>
						</div>
					{{/if}}

				</div>
			{{else if this.isAdvancedTabActive}}
				<div class="tab-2">

					{{#if this.showDataAggregationSettingsWithDatasetsLimitation}}
						<div class="subtitle">Data type</div>

						<div class="row">
							<MetricEditMetricTypePropertiesComponent
								@model={{hash
									metricType=@model.metricType
									aggregationFunction=@model.aggregationFunction
									granulationOrigin=@model.granulationOrigin
								}}
								@submitInProgress={{this.submitInProgress}}
								@onChange={{this.updateMetricTypeProperties}}
								@hidePeriodDataType={{@isPeriodDataTypeHidden}}
								@hideAllValuesDataType={{@isAllValuesDataTypeHidden}}
							/>
							<p>
								These selections determine how your data is handled by
								{{this.accountService.shownCompanyName}}. This especially impacts
								the way data is grouped and displayed for longer date ranges.
								{{#if this.showReadMore}}
									<a
										href="https://developers.databox.com/metric-definition#aggregation-functions"
										target="_blank"
									>
										Read more
									</a>
								{{/if}}
							</p>
						</div>

						{{#if this.showEnsureUnique}}
							<div class="row">
								<MetricEditEnsureUniquenessComponent
									@model={{hash
										ensureUnique=@model.ensureUnique
										isAppend=@model.isAppend
									}}
									@sourceType={{this.sourceType}}
									@submitInProgress={{this.submitInProgress}}
									@onChange={{this.changeEnsureUniquenessSettings}}
								/>
							</div>
						{{/if}}

						{{#if this.showHistoricalDataPersistence}}
							<MetricEditHistoricalDataPersistenceComponent
								@selected={{this.historicalDataPersistence}}
								@submitInProgress={{this.submitInProgress}}
								@onChange={{this.changeHistoricalDataPersistence}}
							/>
						{{/if}}
					{{/if}}

					<div class="subtitle">Other settings</div>

					<div class="row">
						<FormCheckboxComponent
							@name="no_reset"
							@isDisabled={{this.submitInProgress}}
							@selected={{this.dailyZero}}
							@label="Start with zero (0) every day"
							@onChange={{this.changeDailyZero}}
							@item={{hash
								tooltip="Some metrics are not starting with zero (0) at midnight and then increasing or decreasing through the day, like <strong>Account balance</strong>, <strong>Bounce rate</strong> and all <strong>Conversion rates</strong>. For such metrics <strong>a cumulative chart is not available</strong> and cannot be created by summing values for periods."
								tooltipMaxWidth=230
							}}
						/>
					</div>

					{{#if this.showDataAggregationSettings}}

						<div class="row">
							<FormCheckboxComponent
								@name="has_future_data"
								@isDisabled={{this.submitInProgress}}
								@selected={{@model.hasFutureData}}
								@label="Show future date ranges"
								@onChange={{this.changeHasFutureData}}
								@item={{hash
									tooltip="Show future date ranges (Next Week, Next Month, etc.) as date range options for this metric."
									tooltipMaxWidth=230
								}}
							/>
						</div>

					{{/if}}
					{{#if this.showDataAggregationSettingsWithDatasetsLimitation}}

						<div class="row">
							<FormCheckboxComponent
								@name="is_history_changing"
								@isDisabled={{this.submitInProgress}}
								@selected={{@model.isHistoryChanging}}
								@label="Update historical data"
								@onChange={{this.changeIsHistoryChanging}}
								@item={{hash
									tooltip="By disabling this option all historical periods (Last Month, Last Year) won’t be recalculated every time they are requested (just once), even if there is new data stored to the warehouse. This is for optimization purposes and should be turned on for most of the cases."
									tooltipMaxWidth=230
								}}
							/>
						</div>

						{{!--

                <div class="subtitle">Available granulations (daily, weekly, ...)</div>

                <div class="row">
                    <Form::RadiobuttonGroup
                        class="radiobutton-default"
                        @name="granulations_available_section"
                        @selected={{this.selectedGranulationsAvailableSection}}
                        @options={{this.granulationsAvailableSectionOptions}}
                        @isDisabled={{this.submitInProgress}}
                        @onChange={{this.changeGranulationsAvailableSection}}
                    />

                    {{#if this.showAvailableGranulationsSelector}}
                        <Form::Select
                            name="granulations_available"
                            @selected={{@model.granulationsAvailable}}
                            @options={{this.granulationsAvailableOptions}}
                            @onChange={{this.changeGranulationsAvailable}}
                            @isMultiple={{true}}
                            @isDisabled={{this.submitInProgress}}
                        />
                    {{/if}}
                </div>

                --}}

					{{/if}}

				</div>
			{{else if this.isVisualTabActive}}
				<div class="tab-3">
					<MetricEditInvertComponent
						@value={{@model.changeInvert}}
						@isDisabled={{this.submitInProgress}}
						@showReadMore={{this.showReadMore}}
						@onChange={{this.changeChangeInvert}}
					/>

					<div class="subtitle">Default format settings</div>

					<div class="row">
						<MetricEditFormatComponent
							@selected={{@model.format}}
							@onChange={{this.changeFormat}}
							@isDisabled={{this.submitInProgress}}
						/>
						<p>
							This is the default setting on how to format
							<strong>metric values</strong>. You can still manually change it for
							individual datablocks in the advanced settings.
							{{#if this.showReadMore}}
								<a
									href="https://help.databox.com/overview-advanced-settings#format-definition"
									target="_blank"
								>
									Read more
								</a>
							{{/if}}
							<br /><br />
						</p>

					</div>

					<div class="row parts-2">
						<div class="part">
							<FormSelectComponent
								name="change_function"
								@placeholder="Default comparison function"
								@selected={{@model.changeFunction}}
								@options={{this.changeFunctionOptions}}
								@onChange={{this.changeChangeFunction}}
								@isDisabled={{this.submitInProgress}}
								@isRequired={{true}}
							/>
						</div>
						<div class="part">
							<FormSelectFormatComponent
								name="change_format"
								@placeholder="Default comparison number format"
								@selected={{this.selectedChangeFormat}}
								@options={{this.changeFormatOptions}}
								@onChange={{this.changeChangeFormat}}
								@onAdd={{this.addCustomChangeFormat}}
								@isDisabled={{this.submitInProgress}}
							/>
						</div>

						<p>
							This is the default setting on how to calculate and format
							<strong>comparison values</strong>. You can still manually change it for
							individual datablocks in the advanced settings.
							{{#if this.showReadMore}}
								<a
									href="https://help.databox.com/overview-advanced-settings#change-function"
									target="_blank"
								>
									Read more
								</a>
							{{/if}}
						</p>
					</div>

				</div>
			{{/if}}

			<div class="controls2">
				<div class="controls2__right">
					{{#if this.showSaveControls}}
						<SubmitButtonComponent
							@text="Cancel"
							@theme="grey-empty"
							@noDelay={{true}}
							@onTrigger={{this.close}}
						/>
						<SubmitButtonComponent
							@text="Save"
							@onTrigger={{this.save}}
							@submitInProgress={{this.submitInProgress}}
						/>
					{{else}}
						<SubmitButtonComponent
							@text="OK"
							@theme="grey-outlined"
							@onTrigger={{this.close}}
						/>
					{{/if}}
				</div>
			</div>

			{{#if this.showLinkToDevelopersWebsite}}
				<div class="moreInfo single">
					<hr class="wider" />
					<p>
						<CommonUiIconComponent @name="Book" @size="lg" />
						Read more about creating custom metrics<br />
						<a target="_blank" href="https://developers.databox.com">Developers website</a>
					</p>
				</div>
			{{/if}}

		</div>
	</template>
}
